import { NgIf, NgIfContext } from '@angular/common';
import { computed, Directive, effect, inject, input, TemplateRef } from '@angular/core';
import { AuthStore } from './auth.store';

/**
 * Structural directive: renders the host element only when the current user has at least one of
 * the supplied roles. PLATFORM_ADMIN bypasses every check (the bypass lives in
 * {@code AuthStore.hasAnyRole} so all callsites — guards, directives, computed signals — share
 * the same rule).
 *
 * V7: the legacy `libHasRoleIsSuperAdmin` input was removed along with the SUPERADMIN /
 * WHEERE_MAINTAINER aliases. Use `*libHasRole="['PLATFORM_ADMIN']"` for the same effect.
 */
@Directive({
  selector: '[libHasRole]',
  standalone: true,
  hostDirectives: [NgIf],
})
export class HasRoleDirective {
  libHasRole = input.required<string | string[]>();
  libHasRoleElse = input<TemplateRef<NgIfContext<unknown>>>();

  #store = inject(AuthStore);
  #ngIf = inject(NgIf, { host: true });

  private readonly showTemplate = computed(() => {
    if (!this.#store.isLoggedIn()) {
      return false;
    }
    return !!this.#store.hasAnyRole(this.libHasRole());
  });

  constructor() {
    effect(() => {
      this.#ngIf.ngIf = this.showTemplate();
      if (this.libHasRoleElse()) {
        this.#ngIf.ngIfElse = this.libHasRoleElse()!;
      }
    });
  }
}
